/**
 * Task Store — all task state, messages, interactions, and actions.
 *
 * Uses Immer middleware for immutable updates with mutable syntax.
 * enableMapSet() in immerSetup.ts allows direct Map mutations on drafts.
 *
 * The store body is a LIST OF ACTIONS, deliberately. Anything an action needs
 * more than a few lines to say lives in its own module and is handed the
 * `{ get, set }` seam:
 *   - handlers/sendMessage        — the send path (new task / continuation / teammate)
 *   - handlers/respondToInteraction — the unified HITL resolve
 *   - handlers/taskResend         — retryMessage / retryConnection
 *   - taskSync                    — the level-triggered MESSAGE_SYNC request rail
 *   - taskFactory                 — buildNewTask, the one TaskState birth site
 *   - interactionOutcome          — UI response → SDK wire outcome
 *   - taskStoreTypes              — the state/action shape those modules type against
 * They take the seam rather than importing this file because the dependency
 * runs store → module; importing back would close a cycle.
 */

import "./immerSetup";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { EVENTS } from "@/constants/events";
import { posthog } from "@/lib/posthog";
import { useBrowserStore } from "./browserStore";
import { ANALYTICS_EVENTS } from "@bash/shared";
import { DEFAULT_AGENT } from "./types";
import {
  setStoredTaskId,
  schedulePersist,
  flushPersist,
  evictLRU,
} from "./persistence";
import { settleMainPending } from "./pendingQueue";
import { getSocket, registerSelectedTaskIdAccessor } from "./socketService";
import { buildNewTask } from "./taskFactory";
import {
  readInitialTasks,
  readInitialSelectedTaskId,
  runStaleCleanup,
} from "./taskStoreInit";
import { requestTaskSync } from "./taskSync";
import { sendMessage } from "./handlers/sendMessage";
import { respondToInteraction } from "./handlers/respondToInteraction";
import { retryConnection, retryMessage } from "./handlers/taskResend";
import type { PendingInteraction } from "./types";
import type { TaskStore, TaskStoreSeam } from "./taskStoreTypes";

// Re-exported so the many callers of these (TaskView, ModelSelector,
// streamingHandlers, syncHandlers, composerSeed, tests) keep one import site
// even though the implementations moved to their own modules.
export { requestTaskSync, ackTaskSync } from "./taskSync";
export { buildNewTask } from "./taskFactory";
export type { PermissionScope } from "./taskStoreTypes";

// Boot-time browser reads live in ./taskStoreInit — same order as when they
// were inline here: rehydrate, then recover the URL's task, then sweep.
const initialTasks = readInitialTasks();
const initialSelectedTaskId = readInitialSelectedTaskId();
runStaleCleanup();

export const useTaskStore = create<TaskStore>()(
  devtools(
    immer((set, get) => {
      // The pair every extracted action body receives. Bound once so each
      // delegation below is a single line.
      const seam: TaskStoreSeam = { get, set };
      return {
        // Initial state
        tasks: initialTasks,
        selectedTaskId: initialSelectedTaskId,
        pendingNewTaskId: null,
        nextTaskAccessLevel: null,
        pendingSendTaskId: null,
        lastTraceId: null,
        resumeError: null,
        interactionError: null,
        draftInput: null,
        stagedMessages: new Map(),
        depositChips: new Map(),
        teammateParentByTaskId: {},

        // =========================================================================
        // Internal Helpers
        // =========================================================================

        _mutateTasks: (mutator) => {
          set((state) => {
            mutator(state.tasks);
            // LRU eviction on the draft
            if (state.tasks.size > 20) {
              const evicted = evictLRU(
                new Map(state.tasks),
                state.selectedTaskId
              );
              if (evicted.size < state.tasks.size) {
                state.tasks.clear();
                for (const [k, v] of evicted) state.tasks.set(k, v);
              }
            }
          });
          // Defer persistence during active streaming — the rAF buffer calls
          // _mutateTasks every frame, and serializing the entire task Map 60x/s
          // is wasteful. Persist only when no task is actively streaming.
          const tasks = get().tasks;
          let anyStreaming = false;
          for (const task of tasks.values()) {
            if (task.streamingMessage) {
              anyStreaming = true;
              break;
            }
          }
          if (!anyStreaming) {
            schedulePersist(tasks);
          }
        },

        _setTaskPhase: (taskId, phase) => {
          get()._mutateTasks((tasks) => {
            const task = tasks.get(taskId);
            if (task) task.phase = phase;
          });
        },

        _createTask: (taskId, agentSlug = DEFAULT_AGENT, initialData = {}) => {
          const newTask = buildNewTask(taskId, agentSlug, initialData);
          get()._mutateTasks((tasks) => {
            tasks.set(taskId, newTask);
          });
          return newTask;
        },

        _setSelectedTaskId: (taskId) => {
          set((state) => {
            state.selectedTaskId = taskId;
          });
          setStoredTaskId(taskId);
        },

        // =========================================================================
        // Public Actions
        // =========================================================================

        sendMessage: (content, images, files, options) =>
          sendMessage(seam, content, images, files, options),

        cancelQuery: (source) => {
          const { selectedTaskId } = get();
          if (!selectedTaskId) return;
          get().cancelTask(selectedTaskId, source);
        },

        cancelTask: (taskId, source) => {
          const socket = getSocket();
          const { tasks } = get();
          // The two ways this reaches nothing. Reported to the caller (M4): the
          // helpers bar closes its question on the answer, and a click that emitted
          // nothing must not dismiss the ask as if it had worked.
          if (!taskId || !socket?.connected) return false;
          const task = tasks.get(taskId);
          // A task WAITING ON THE PERSON is stoppable too (N1) — but only from a
          // HELPER's own Stop (R85). A pending interaction parks its lane at
          // phase 'idle' (stampPending), so the three live phases alone read a
          // paused turn as finished while the runner is still holding it; that
          // gap made a background helper's Stop a dead button on every surface.
          // The pass is scoped to `source: 'helper'`, so Escape on a paused main
          // turn is refused exactly as it always was, and so is the composer's
          // own Stop.
          const stoppable =
            task?.phase === "processing" ||
            task?.phase === "waiting" ||
            task?.phase === "interrupting" ||
            (source === "helper" && !!task?.pendingInteraction);
          if (!stoppable) return false;

          // `source` tells the runner WHO pulled it: Esc gets a short grace right after
          // a steer (a second Esc on an already-drained queue was a send, not a stop);
          // the Stop button always stops. Omitted = button (old-client semantics).
          // The WIRE vocabulary is the runner's two words, pinned by the zod
          // enum in packages/shared (event-payloads.schema.ts): 'esc' takes a
          // post-steer grace, anything else always stops. 'helper' is a
          // CLIENT-side intent — which surface pulled it — so it rides as the
          // button it is, and the payload stays byte-identical to before.
          const wireSource = source === "helper" ? "button" : source;
          socket.emit(EVENTS.AGENT_INTERRUPT, {
            sessionId: taskId,
            targetAgent: DEFAULT_AGENT,
            ...(wireSource ? { source: wireSource } : {}),
          });

          // Only set phase on first cancel — subsequent clicks re-send the interrupt
          // without resetting phase (keeps UI in "interrupting" state)
          if (task?.phase !== "interrupting") {
            get()._mutateTasks((tasks) => {
              const t = tasks.get(taskId);
              if (t) t.phase = "interrupting";
            });
          }
          return true;
        },

        cancelStaged: (taskId, uuid) => {
          const socket = getSocket();
          if (!socket?.connected) return;
          // Optimistically drop the card; the agent's CANCEL is idempotent-by-uuid,
          // and the next STAGING_SYNC reconciles if the item was already picked up.
          set((state) => {
            const list = (state.stagedMessages.get(taskId) || []).filter(
              (m) => m.uuid !== uuid
            );
            if (list.length === 0) state.stagedMessages.delete(taskId);
            else state.stagedMessages.set(taskId, list);
          });
          socket.emit(EVENTS.CANCEL_STAGED, { taskId, uuid });
        },

        requestStagingSync: (taskId) => {
          const socket = getSocket();
          if (!socket?.connected) return;
          socket.emit(EVENTS.STAGING_SYNC_REQUEST, { taskId });
        },

        answerQuestion: (_toolUseId, answers, subagentId, targetTaskId) => {
          get()._respondToInteraction(
            { type: "question", answers },
            subagentId || targetTaskId
              ? { subagentId, taskId: targetTaskId }
              : undefined
          );
        },

        respondToApproval: (_toolUseId, approved, subagentId, targetTaskId) => {
          get()._respondToInteraction(
            { type: "approval", approved },
            subagentId || targetTaskId
              ? { subagentId, taskId: targetTaskId }
              : undefined
          );
        },

        respondToPermission: (
          _toolUseId,
          approved,
          subagentId,
          scope,
          targetTaskId
        ) => {
          get()._respondToInteraction(
            { type: "permission", approved, ...(scope && { scope }) },
            subagentId || targetTaskId
              ? { subagentId, taskId: targetTaskId }
              : undefined
          );
        },

        respondToHandsApproval: (interactionId, approved, targetTaskId) => {
          // Addressed by interaction id (server-gate rows have no live toolUseId). The
          // nonce echo is built in buildInteractionOutcome from the pending's `nonce`.
          get()._respondToInteraction(
            { type: "hands", approved },
            { interactionId, taskId: targetTaskId }
          );
        },

        setTaskAccessLevel: (taskId, level) => {
          // Optimistic: reflect the override immediately so the picker stops showing
          // "(default)"; the server persists it and forwards the effective level.
          get()._mutateTasks((tasks) => {
            const t = tasks.get(taskId);
            if (t) t.accessLevel = level;
          });
          getSocket()?.emit(EVENTS.SET_ACCESS_LEVEL, { taskId, level });
        },

        setNextTaskAccessLevel: (level) => {
          set((state) => {
            state.nextTaskAccessLevel = level;
          });
        },

        resolveConnection: (toolkit, explicitTaskId, interactionId) => {
          const targetTaskId = explicitTaskId || get().selectedTaskId;
          // An interaction id alone is enough to resolve (it addresses the row
          // directly), so don't bail when there's no live/selected task.
          if (!targetTaskId && !interactionId) return;
          // Phase 9: delegate entirely to _respondToInteraction → resolveInteractionApi.
          // The legacy CONNECTION_RESOLVED socket emit is gone.
          get()._respondToInteraction(
            { type: "auth", toolkit },
            { taskId: targetTaskId ?? undefined, interactionId }
          );
        },

        declineConnection: (toolkit, explicitTaskId, interactionId) => {
          const targetTaskId = explicitTaskId || get().selectedTaskId;
          if (!targetTaskId && !interactionId) return;
          // User skipped/dismissed the connect prompt. Substrate resolves with
          // behavior:'deny' so the agent can choose a fallback path (politely back
          // off, propose alternatives) instead of hanging on the auth gate forever.
          get()._respondToInteraction(
            { type: "auth", toolkit, approved: false },
            { taskId: targetTaskId ?? undefined, interactionId }
          );
        },

        clearPendingInteraction: (taskId) => {
          get()._mutateTasks((tasks) => {
            const t = tasks.get(taskId);
            // Settle the currently-displayed head; a queued sibling ask (parallel
            // tool batch) is promoted so the user is walked through all of them.
            if (t) settleMainPending(t);
          });
        },

        reconcileServerInteraction: (taskId, interactionId) => {
          if (!taskId || !interactionId) return;
          get()._mutateTasks((tasks) => {
            const t = tasks.get(taskId);
            if (!t) return;
            // Match the live pending against the row the server just settled. Prefer
            // the Input Station interactionId; fall back to requestId for legacy
            // pendings that carry no interactionId. Scope strictly so a stale or
            // unrelated interaction can never retract an active picker.
            const matches = (pi: PendingInteraction) =>
              (pi.interactionId ?? pi.requestId) === interactionId;
            // Main (`''`) lane — the visible head OR a queued sibling ask. Settling
            // the head promotes the next queued interaction; settling a sibling
            // leaves the visible card alone.
            if (settleMainPending(t, interactionId)) {
              // After a Stop the server withdrew the ask itself, and its row
              // often lands after the Stop's terminal: nothing resumes, so it
              // must not put "picking up your answer" back (task f517766b).
              if (t.turnEndedInterrupted) t.resumingFrom = null;
              return;
            }
            // Subagent-deep-HITL (T9) — retract only the matching SUBAGENT lane; the
            // interaction id is globally unique, so at most one lane matches and the
            // siblings' pending pickers stay up.
            if (t.pendingByLane) {
              for (const [laneId, pi] of Object.entries(t.pendingByLane)) {
                if (matches(pi)) {
                  delete t.pendingByLane[laneId];
                  return;
                }
              }
            }
          });
        },

        switchTask: (newTaskId, taskHistory) => {
          posthog.capture(ANALYTICS_EVENTS.TASK_RESUMED, {
            task_id: newTaskId,
          });
          const { tasks } = get();

          if (!tasks.has(newTaskId)) {
            const historyTask = taskHistory?.find((t) => t.id === newTaskId);
            const newTask = buildNewTask(
              newTaskId,
              historyTask?.agentSlug || DEFAULT_AGENT,
              {
                title: historyTask?.title || "Task",
                phase: "idle",
                // Hydrate the locked engine + model from the history list (fast path for
                // tasks opened from the sidebar). MESSAGE_SYNC re-confirms on deep-link.
                // Carrying model here is what lets the pill speak truth immediately rather
                // than masking to a default until the sync round-trip lands.
                ...(historyTask?.runtime
                  ? { runtime: historyTask.runtime }
                  : {}),
                ...(historyTask?.model ? { model: historyTask.model } : {}),
                // Same fast path for the agent's face: the list row's agent id rides along
                // when a caller hands the history in, matching the neighbours above, though
                // today no production caller passes one.
                ...(historyTask?.agentId !== undefined
                  ? { agentId: historyTask.agentId }
                  : {}),
                ...(historyTask?.accessLevel !== undefined
                  ? { accessLevel: historyTask.accessLevel }
                  : {}),
                // Same fast path for the App tab: the list DTO already carries the
                // pointer, so a sidebar open shows the dormant app immediately instead
                // of popping the tab in one MESSAGE_SYNC later.
                ...(historyTask?.livePreview !== undefined
                  ? { livePreviewPointer: historyTask.livePreview }
                  : {}),
                // Same fast path for the "via <channel>" chip: the list DTO already
                // knows the door, so opening a channel-born task from the board shows
                // its provenance immediately instead of one MESSAGE_SYNC later.
                ...(historyTask?.originChannel
                  ? { originChannel: historyTask.originChannel }
                  : {}),
                // Engines T5: a task paused on its plan shows its waiting line
                // from the list row, before MESSAGE_SYNC lands.
                ...(historyTask?.resumeAt !== undefined
                  ? { resumeAt: historyTask.resumeAt }
                  : {}),
                // Several accounts: the header names the account from the list row.
                ...(historyTask?.engineSlotId !== undefined
                  ? { engineSlotId: historyTask.engineSlotId }
                  : {}),
              }
            );
            set((state) => {
              state.tasks.set(newTaskId, newTask);
              state.selectedTaskId = newTaskId;
              // Navigating to a different task ends the in-flight send window;
              // pendingSendTaskId is no longer a relevant bridge (urlTaskId will
              // catch up through the proper Next.js route change).
              if (
                state.pendingSendTaskId &&
                state.pendingSendTaskId !== newTaskId
              ) {
                state.pendingSendTaskId = null;
              }
            });
            // LRU eviction outside Immer (needs pure Map for evictLRU)
            const currentTasks = get().tasks;
            if (currentTasks.size > 20) {
              const evicted = evictLRU(new Map(currentTasks), newTaskId);
              if (evicted.size < currentTasks.size) {
                set((state) => {
                  state.tasks.clear();
                  for (const [k, v] of evicted) state.tasks.set(k, v);
                });
              }
            }
            schedulePersist(get().tasks);
          } else {
            set((state) => {
              state.selectedTaskId = newTaskId;
              if (
                state.pendingSendTaskId &&
                state.pendingSendTaskId !== newTaskId
              ) {
                state.pendingSendTaskId = null;
              }
            });
          }
          setStoredTaskId(newTaskId);

          // ALWAYS sync on switch — cache is a render hint, never a gate (the old
          // "skip if messages are cached" gate starved model/runtime/todos and left
          // the ModelSelector pill on "…", founder-hit 07-21). Routed through the
          // shared de-duped helper because switchTask is NOT on every open path —
          // TaskView short-circuits it when the created task is already selected
          // (the notch/composer-born flow) — so the TaskView URL effect fires the
          // same helper.
          requestTaskSync(newTaskId);
          const socket = getSocket();
          if (socket?.connected) {
            // Refresh sandbox state so status indicator shows current reality
            socket.emit(EVENTS.CONNECTION_STATE_QUERY);
          }
        },

        createNewTask: () => {
          set((state) => {
            state.selectedTaskId = null;
            state.pendingSendTaskId = null;
            // Clear the draft UUID so the next typing session allocates fresh.
            // The previous draft (if any) is abandoned; its sandbox SDK session
            // will idle out in 24s.
            state.pendingNewTaskId = null;
            // Abandon the composer's draft access override too — a fresh draft
            // starts from the user default.
            state.nextTaskAccessLevel = null;
          });
          setStoredTaskId(null);
        },

        getOrAllocatePendingNewTaskId: () => {
          const existing = get().pendingNewTaskId;
          if (existing) return existing;
          const fresh = crypto.randomUUID();
          set((state) => {
            state.pendingNewTaskId = fresh;
          });
          return fresh;
        },

        /**
         * Create an empty task whose purpose is "have a browser session open."
         * Used when the user clicks the browser toggle on the new-task page —
         * we commit to a real task immediately (with a meaningful title) instead
         * of holding pending intent. The layout's selectedTaskId-watcher routes
         * the URL to /task/[uuid] within one frame.
         */
        createBrowserSessionTask: () => {
          if (get().pendingSendTaskId) return get().pendingSendTaskId!;
          const taskId = crypto.randomUUID();
          set((state) => {
            state.pendingSendTaskId = taskId;
            state.tasks.set(
              taskId,
              buildNewTask(taskId, DEFAULT_AGENT, {
                title: "Browser session",
                phase: "idle",
              })
            );
            state.selectedTaskId = taskId;
          });
          // Analytics v1 retired task:created. An empty browser-session task is
          // not an ask, so it sends no delegation:submitted either.
          setStoredTaskId(taskId);
          return taskId;
        },

        deleteTask: (taskId) => {
          const socket = getSocket();
          if (!socket?.connected) {
            console.error("[deleteTask] Socket not connected");
            return;
          }

          const { selectedTaskId } = get();
          set((state) => {
            state.tasks.delete(taskId);
            if (state.selectedTaskId === taskId) state.selectedTaskId = null;
          });
          schedulePersist(get().tasks);
          if (selectedTaskId === taskId) setStoredTaskId(null);

          // If this task held a browser panel slot, clean it up. When it was
          // the last open slot we ALSO need to tell the server to tear down
          // the user's browser session — TASK_DESTROY alone doesn't infer
          // browser-close server-side. Without this, deleting a task with the
          // panel open leaves the 'browsing' display overlay sticky and leaks
          // the sandbox-side Chrome session.
          const { wasLastAndSessionLive } = useBrowserStore
            .getState()
            .cleanupForDeletedTask(taskId);
          if (wasLastAndSessionLive) {
            socket.emit(EVENTS.BROWSER_CLOSE_SESSION, {});
          }

          // TASK_DESTROYED is emitted server-side (task-handlers on TASK_DESTROY),
          // which is authoritative — capturing it here too double-counts.
          socket.emit(
            EVENTS.TASK_DESTROY,
            { taskId },
            (ack: { success: boolean; error?: string }) => {
              if (!ack?.success)
                console.error("[deleteTask] Server error:", ack?.error);
            }
          );
        },

        updateTaskTitle: (taskId, title) => {
          const socket = getSocket();
          get()._mutateTasks((tasks) => {
            const task = tasks.get(taskId);
            if (task) task.title = title;
          });
          posthog.capture(ANALYTICS_EVENTS.TASK_TITLE_EDITED, {
            task_id: taskId,
          });
          socket?.emit(EVENTS.TASK_TITLE_EDIT, { taskId, title });
        },

        retryMessage: (messageId) => retryMessage(seam, messageId),

        retryConnection: () => retryConnection(seam),

        dismissResumeError: () => {
          set((state) => {
            state.resumeError = null;
          });
        },

        dismissInteractionError: () => {
          set((state) => {
            state.interactionError = null;
          });
        },

        setDraftInput: (text) => {
          set((state) => {
            state.draftInput = text;
          });
        },

        // =========================================================================
        // Private: Unified Interaction Response
        // =========================================================================

        _respondToInteraction: (response, target) =>
          respondToInteraction(seam, response, target),
      };
    }),
    { name: "TaskStore", enabled: process.env.NODE_ENV === "development" }
  )
);

// Register selectedTaskId accessor with socketService so connectionStore can read it
// without importing taskStore (which would create a circular dependency).
registerSelectedTaskIdAccessor(() => useTaskStore.getState().selectedTaskId);

/**
 * The user's OWN task stream carried this task's row (TASK_STREAM, tasks:list,
 * TASK_CREATED: the server scopes every one of them to the owner), which is
 * proof the task exists and is theirs. A no-access screen is a belief formed
 * from one refusal, so this proof lifts it (staging drill 10-01, task
 * 39865d34: a new task's first sync outran its row insert, TASK_NOT_FOUND
 * marked it denied, and nothing ever cleared it). It also re-asks the sync a
 * newborn this client minted is still waiting on. Only the task on screen is
 * re-synced; any other entry syncs when it is opened (switchTask).
 */
export function vouchForOwnTask(taskId: string): void {
  const state = useTaskStore.getState();
  const task = state.tasks.get(taskId);
  if (!task) return;
  const awaitingRow = !task.hasSynced && task.mintedHereAt !== undefined;
  if (!task.accessDenied && !awaitingRow) return;
  if (task.accessDenied) {
    state._mutateTasks((tasks) => {
      const t = tasks.get(taskId);
      if (t) t.accessDenied = false;
    });
  }
  if (taskId === state.selectedTaskId || taskId === state.pendingSendTaskId) {
    requestTaskSync(taskId);
  }
}

// ─── Granular Selectors ─────────────────────────────────────────
// Avoid re-rendering on every task mutation.

/** Stable array of task IDs — only re-renders when tasks are added/removed */
export const useTaskIds = () =>
  useTaskStore(useShallow((s) => Array.from(s.tasks.keys())));

/** Single task by ID — only re-renders when THIS task changes */
export const useTask = (taskId: string | null) =>
  useTaskStore((s) => (taskId ? (s.tasks.get(taskId) ?? null) : null));

/** Task phase only — cheap selector for status indicators */
export const useTaskPhase = (taskId: string) =>
  useTaskStore((s) => s.tasks.get(taskId)?.phase ?? "idle");

// Flush debounced persistence on tab close so we don't lose the last 2s of state
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    flushPersist(useTaskStore.getState().tasks);
  });
}
